import React, { useEffect } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { useNavigate, useParams, Link, Navigate } from 'react-router-dom';
import { SITE_CONFIG, SEO_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import { guides } from '../data/guides';
import { neighborhoods } from '../data/neighborhoods';
import './IDXTownPage.css';

const AGENT_FIRST_NAME = SITE_CONFIG.AGENT.NAME.split(' ')[0];

// Route: /guide/:slug — data-driven SEO/AEO articles from src/data/guides.js
const GuidePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const guide = guides[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  if (!guide) return <Navigate to="/" replace />;

  const canonical = `${SEO_CONFIG.DOMAIN}/guide/${guide.slug}`;
  const relatedArea = guide.relatedNeighborhood ? neighborhoods[guide.relatedNeighborhood] : null;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.metaDescription,
    url: canonical,
    author: { '@type': 'Person', name: SITE_CONFIG.AGENT.NAME },
    publisher: { '@type': 'RealEstateAgent', name: SITE_CONFIG.AGENT.BROKERAGE, url: SEO_CONFIG.DOMAIN },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical }
  };

  const faqSchema = guide.faqs && guide.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: guide.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  } : null;

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>{guide.metaTitle}</title>
        <meta name="description" content={guide.metaDescription} />
        <meta name="keywords" content={guide.keywords} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={guide.metaTitle} />
        <meta property="og:description" content={guide.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <div className="idxtown-container">
        <button className="idxtown-home-btn" onClick={() => navigate('/')}>← Home</button>
        <header className="idxtown-header">
          <span className="idxtown-eyebrow">{guide.eyebrow || 'South Florida Real Estate Guide'}</span>
          <h1>{guide.h1 || guide.title}</h1>
        </header>

        <article className="guide-article">
          <section className="idxtown-vibe">
            {guide.intro.map((para, i) => <p key={i}>{para}</p>)}
          </section>

          {guide.sections.map((sec, i) => (
            <section className="idxtown-vibe" key={i}>
              <h2>{sec.h2}</h2>
              {sec.body.map((para, j) => <p key={j}>{para}</p>)}
              {sec.bullets && (
                <ul className="neighborhood-resources">
                  {sec.bullets.map((b, k) => <li key={k}>{b}</li>)}
                </ul>
              )}
              {sec.links && (
                <ul className="neighborhood-resources">
                  {sec.links.map((link) => <li key={link.url}><a href={link.url} target="_blank" rel="noopener noreferrer">{link.label}</a></li>)}
                </ul>
              )}
            </section>
          ))}

          {guide.faqs && guide.faqs.length > 0 && (
            <section className="idxtown-lifestyle">
              <h2>Frequently Asked Questions</h2>
              <div className="amenities-list">
                {guide.faqs.map((f, i) => (
                  <div className="amenity" key={i}>
                    <h3>{f.q}</h3>
                    <p>{f.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="idxtown-lifestyle">
            <h2>Explore More</h2>
            <ul className="neighborhood-resources">
              {(guide.relatedGuides || []).filter((key) => guides[key]).map((key) => (
                <li key={key}><Link to={`/guide/${key}`}>{guides[key].title}</Link></li>
              ))}
              {relatedArea && (
                <li><Link to={`/neighborhood/${relatedArea.slug}`}>{relatedArea.name} Homes for Sale &amp; Neighborhood Guide</Link></li>
              )}
              <li><Link to="/guides">Browse All Buyer, Seller &amp; Lifestyle Guides</Link></li>
              <li><Link to="/agent">Meet {SITE_CONFIG.AGENT.NAME}</Link></li>
              <li><Link to="/contact-us">Contact {AGENT_FIRST_NAME}</Link></li>
            </ul>
          </section>
        </article>

        <section className="cta-section">
          <h2>Have Questions About Your Move?</h2>
          <p>{SITE_CONFIG.AGENT.NAME} brings {SITE_CONFIG.AGENT_YEARS} years of South Florida experience to every buyer and seller. Reach out for personalized guidance.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}?subject=${encodeURIComponent(guide.title + ' — Question')}`} className="cta-button primary">
              Email {AGENT_FIRST_NAME}
            </a>
            <a href={`tel:${SITE_CONFIG.AGENT.PHONE}`} className="cta-button secondary">
              Call {SITE_CONFIG.AGENT.PHONE.replace('+1-', '')}
            </a>
          </div>
        </section>
      </div>

      <div style={{background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
        <span style={{fontSize: '10px', color: '#ccc', flex: 1}}>{SITE_CONFIG.MLS.DISCLAIMER} | Agent License: {SITE_CONFIG.AGENT.LICENSE}</span>
      </div>
    </div>
  );
};

export default GuidePage;
